import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";

// Public Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import PackagesPage from "./pages/PackagesPage";
import ContactPage from "./pages/ContactPage";
import AgentRegisterPage from "./pages/AgentRegisterPage";
import LoginPage from "./pages/LoginPage";
import QuoteView from "./pages/public/QuoteView"; 

// Agent Dashboard
import AgentDashboard from "./pages/agent/AgentDashboard";
import AgentQuotes from "./pages/agent/AgentQuotes";
import AgentBookings from "./pages/agent/AgentBookings";
import RequestQuote from "./pages/agent/RequestQuote";
import AgentSettings from "./pages/agent/AgentSettings";
import AgentPayments from "./pages/agent/AgentPayments";
import AgentReports from "./pages/agent/AgentReports";
import AgentPackages from "./pages/agent/AgentPackages";
import AgentPackageDetail from "./pages/agent/AgentPackageDetail";
import AgentHotels from "./pages/agent/AgentHotels";

// Admin Dashboard
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminAgents from "./pages/admin/AdminAgents";
import AgentDetail from "./pages/admin/AgentDetail";
import AdminQuotes from "./pages/admin/AdminQuotes";
import QuoteBuilder from "./pages/admin/QuoteBuilder";
import AdminBookings from "./pages/admin/AdminBookings";
import BookingDetail from "./pages/BookingDetail";
import QuoteDetail from "./pages/QuoteDetail";
import AdminReports from "./pages/admin/AdminReports";
import DestinationReport from "./pages/admin/DestinationReport";
import AgentReport from "./pages/admin/AgentReport";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminReminders from "./pages/admin/AdminReminders";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminResources from "./pages/admin/AdminResources";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminFollowUp from "./pages/admin/AdminFollowUp";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminQuotePreview from "./pages/admin/AdminQuotePreview";
import AdminHotels from "./pages/admin/AdminHotels";
import DestinationDetail from "./pages/admin/DestinationDetail";
import HotelRequestDetail from "./pages/HotelRequestDetail";
import TravelNewsPage from "./pages/TravelNewsPage";
import CurrencyRatesPage from "./pages/CurrencyRatesPage";
import ChatPage from "./pages/ChatPage";

// Components
import WhatsAppButton from "./components/WhatsAppButton";
import ProtectedRoute from "./components/ProtectedRoute";
import PWAInstallPopup from "./components/PWAInstallPopup";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/agent-register" element={<AgentRegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/quote/:id" element={<QuoteView />} /> 

            {/* Agent Routes */}
            <Route path="/agent" element={<ProtectedRoute role="agent"><AgentDashboard /></ProtectedRoute>} />
            <Route path="/agent/quotes" element={<ProtectedRoute role="agent"><AgentQuotes /></ProtectedRoute>} />
            <Route path="/agent/quotes/:id" element={<ProtectedRoute role="agent"><QuoteDetail /></ProtectedRoute>} />
            <Route path="/agent/bookings" element={<ProtectedRoute role="agent"><AgentBookings /></ProtectedRoute>} />
            <Route path="/agent/bookings/:id" element={<ProtectedRoute role="agent"><BookingDetail /></ProtectedRoute>} />
            <Route path="/agent/request-quote" element={<ProtectedRoute role="agent"><RequestQuote /></ProtectedRoute>} />
            <Route path="/agent/payments" element={<ProtectedRoute role="agent"><AgentPayments /></ProtectedRoute>} />
            <Route path="/agent/reports" element={<ProtectedRoute role="agent"><AgentReports /></ProtectedRoute>} />
            <Route path="/agent/packages" element={<ProtectedRoute role="agent"><AgentPackages /></ProtectedRoute>} />
            <Route path="/agent/packages/:id" element={<ProtectedRoute role="agent"><AgentPackageDetail /></ProtectedRoute>} />
            <Route path="/agent/hotels" element={<ProtectedRoute role="agent"><AgentHotels /></ProtectedRoute>} />
            <Route path="/agent/hotels/:id" element={<ProtectedRoute role="agent"><HotelRequestDetail type="agent" /></ProtectedRoute>} />
            <Route path="/agent/settings" element={<ProtectedRoute role="agent"><AgentSettings /></ProtectedRoute>} />
            <Route path="/agent/travel-news" element={<ProtectedRoute role="agent"><TravelNewsPage /></ProtectedRoute>} />
            <Route path="/agent/currency-rates" element={<ProtectedRoute role="agent"><CurrencyRatesPage /></ProtectedRoute>} />
            <Route path="/agent/chat" element={<ProtectedRoute role="agent"><ChatPage /></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/packages" element={<ProtectedRoute role="admin" permission="packages"><AdminPackages /></ProtectedRoute>} />
            <Route path="/admin/agents" element={<ProtectedRoute role="admin" permission="agents"><AdminAgents /></ProtectedRoute>} />
            <Route path="/admin/agents/:id" element={<ProtectedRoute role="admin" permission="agents"><AgentDetail /></ProtectedRoute>} />
            <Route path="/admin/quotes" element={<ProtectedRoute role="admin" permission="quotes"><AdminQuotes /></ProtectedRoute>} />
            <Route path="/admin/request-quote" element={<ProtectedRoute role="admin" permission="quotes"><RequestQuote /></ProtectedRoute>} />
            <Route path="/admin/quote-builder/:id" element={<ProtectedRoute role="admin" permission="quotes"><QuoteBuilder /></ProtectedRoute>} />
            <Route path="/admin/quote-preview/:id" element={<ProtectedRoute role="admin" permission="quotes"><AdminQuotePreview /></ProtectedRoute>} />
            <Route path="/admin/bookings" element={<ProtectedRoute role="admin" permission="bookings"><AdminBookings /></ProtectedRoute>} />
            <Route path="/admin/bookings/:id" element={<ProtectedRoute role="admin" permission="bookings"><BookingDetail /></ProtectedRoute>} />
            <Route path="/admin/invoices" element={<ProtectedRoute role="admin" permission="bookings"><AdminInvoices /></ProtectedRoute>} />
            <Route path="/admin/hotels" element={<ProtectedRoute role="admin" permission="bookings"><AdminHotels /></ProtectedRoute>} />
            <Route path="/admin/hotels/:id" element={<ProtectedRoute role="admin" permission="bookings"><HotelRequestDetail type="admin" /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute role="admin" permission="payments"><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/reminders" element={<ProtectedRoute role="admin" permission="payments"><AdminReminders /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute role="admin" permission="reports"><AdminReports /></ProtectedRoute>} />
            <Route path="/admin/destination-report" element={<ProtectedRoute role="admin" permission="reports"><DestinationReport /></ProtectedRoute>} />
            <Route path="/admin/agent-report" element={<ProtectedRoute role="admin" permission="reports"><AgentReport /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute role="admin"><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/resources" element={<ProtectedRoute role="admin" permission="packages"><AdminResources /></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute role="admin"><AdminAnnouncements /></ProtectedRoute>} />
            <Route path="/admin/follow-up" element={<ProtectedRoute role="admin"><AdminFollowUp /></ProtectedRoute>} />
            <Route path="/admin/travel-news" element={<ProtectedRoute role="admin"><TravelNewsPage /></ProtectedRoute>} />
            <Route path="/admin/currency-rates" element={<ProtectedRoute role="admin"><CurrencyRatesPage /></ProtectedRoute>} />
            <Route path="/admin/chat" element={<ProtectedRoute role="admin"><ChatPage /></ProtectedRoute>} />
            <Route path="/admin/destination/:name" element={<ProtectedRoute role="admin"><DestinationDetail /></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" richColors />
          <PWAInstallPopup />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
